<template>
    <div id="merge-report-setting" class="flex justify-center items-center">
        <div @click="closeByBackdrop" class="backdrop"></div>
        <div class="modal-content">
            <div class="modal-header flex justify-between items-center">
                <h1>{{ modalTitle || $t('merge_report_ranking_setting_header') }}</h1>
                <div @click="closeModal" class="close-button flex justify-center items-center cursor-pointer">
                    <i class="fas fa-times"></i>
                </div>
            </div>
            <div class="modal-body">
                <form @submit.prevent="closeModal">
                    <XToggleSwitch
                        v-if="isLeaderboardMode"
                        @input="setting.status = $event.target.checked"
                        :checked="setting.status"
                        :border-checked-active="true"
                        :content="$t('merge_report_label_status')"
                        :disabled="loading"
                        class="text-sm shadow-text-none mb-4 mt-1 inline-flex"
                        height="20"
                        color="#5a5351"
                        border-weight="3"
                        border-color="#5a5351"
                        checked-color="#28a745"
                    />

                    <div v-if="isLeaderboardMode" class="mb-2">
                        <label class="text-sm" for="display-ranking">{{
                            $t('merge_report_label_display_ranking')
                        }}</label>
                        <XInput
                            @input="setting.displayRanking = $event.target.value"
                            :value="setting.displayRanking"
                            :only-number="true"
                            :placeholder="$t('merge_report_placeholder_enter_number')"
                            :data-vv-as="$t('merge_report_label_display_ranking')"
                            :disabled="loading"
                            v-validate="{
                                rules: { required: true, max_value: 1000, min_value: 3 }
                            }"
                            name="display-ranking"
                            class="setting-input mt-1"
                            id="display-ranking"
                        />
                        <span v-if="errors.has('display-ranking')" class="block mt-1 text-sm text-red">
                            <i class="fas fa-exclamation-circle"></i>
                            {{ errors.first('display-ranking') }}
                        </span>
                    </div>
                    <div v-if="isLeaderboardMode" class="mb-2">
                        <label class="text-sm" for="reload-time">
                            {{ $t('merge_report_label_reload_time') }} ({{ $t('merge_report_label_reload_time_unit') }})
                            <i
                                v-tippy="{ placement: 'top' }"
                                :content="$t('merge_report_label_reload_tooltip')"
                                class="fas fa-info-circle cursor-pointer"
                            ></i>
                        </label>
                        <XInput
                            @input="setting.reloadTime = $event.target.value"
                            :value="setting.reloadTime"
                            :only-number="true"
                            :placeholder="$t('merge_report_placeholder_enter_number')"
                            :data-vv-as="$t('merge_report_label_reload_time')"
                            :disabled="loading"
                            v-validate="{
                                rules: { required: true, max_value: 99, min_value: 1 }
                            }"
                            name="reload-time"
                            class="setting-input mt-1"
                            id="reload-time"
                        />
                        <span v-if="errors.has('reload-time')" class="block mt-1 text-sm text-red">
                            <i class="fas fa-exclamation-circle"></i>
                            {{ errors.first('reload-time') }}
                        </span>
                    </div>
                    <div v-if="isLeaderboardMode" class="mb-2">
                        <label class="text-sm" for="show-ranking-time">
                            {{ $t('merge_report_label_show_ranking_time') }} ({{
                                $t('merge_report_label_show_ranking_time_unit')
                            }})
                            <i
                                v-tippy="{ placement: 'top' }"
                                :content="$t('merge_report_label_show_ranking_tooltip')"
                                class="fas fa-info-circle cursor-pointer"
                            ></i>
                        </label>
                        <XInput
                            @input="setting.showRankingTime = $event.target.value"
                            :value="setting.showRankingTime"
                            :only-number="true"
                            :placeholder="$t('merge_report_placeholder_enter_number')"
                            :data-vv-as="$t('merge_report_label_show_ranking_time')"
                            :disabled="loading"
                            v-validate="{
                                rules: { required: true, max_value: 60, min_value: 10 }
                            }"
                            name="show-ranking-time"
                            class="setting-input mt-1"
                            id="show-ranking-time"
                        />
                        <span v-if="errors.has('show-ranking-time')" class="block mt-1 text-sm text-red">
                            <i class="fas fa-exclamation-circle"></i>
                            {{ errors.first('show-ranking-time') }}
                        </span>
                    </div>
                    <div v-if="isLeaderboardMode" class="mb-2">
                        <label class="text-sm" for="display-layout">{{ $t('merge_report_label_layout') }}</label>
                        <Selection
                            v-model="setting.layout"
                            @input="setting.layout = $event"
                            :root="{ backgroundColor: 'rgba(255, 255, 255, .5)' }"
                            :select="{ fontSize: '14px', color: '#3f5c56', textAlignLast: 'left' }"
                            :class="{ 'pointer-events-none': loading }"
                            style="padding: 0.1rem"
                            class="setting-input w-full mt-1"
                            id="display-layout"
                        >
                            <option class="text-center" value="list" selected>
                                {{ $t('merge_report_layout_list') }}
                            </option>
                            <option class="text-center" value="podium">
                                {{ $t('merge_report_layout_podium') }}
                            </option>
                            <option class="text-center" value="perpage">
                                {{ $t('merge_report_layout_perpage_label', { perpage: 10 }) }}
                            </option>
                        </Selection>
                    </div>
                    <div class="mb-2">
                        <label class="text-sm" for="merge-player-option">
                            {{ $t('merge_report_label_custom_report_display') }}
                        </label>
                        <Selection
                            v-model="setting.mergePlayerOption"
                            @input="setting.mergePlayerOption = $event"
                            :root="{ backgroundColor: 'rgba(255, 255, 255, .5)' }"
                            :select="{ fontSize: '14px', color: '#3f5c56', textAlignLast: 'left' }"
                            :class="{ 'pointer-events-none': loading }"
                            style="padding: 0.1rem"
                            class="setting-input w-full mt-1"
                            id="merge-player-option"
                        >
                            <option class="text-center" value="same_name_device_id">
                                {{ $t('merge_report_option_same_name_device_id') }}
                            </option>
                            <option class="text-center" value="dont_merge">
                                {{ $t('merge_report_option_treat_every_reponse_as_unique') }}
                            </option>
                        </Selection>
                        <div>
                            <p class="opacity-75 text-xs">
                                <i class="fas fa-info-circle"></i>
                                {{
                                    $t(
                                        setting.mergePlayerOption == 'dont_merge'
                                            ? 'merge_report_option_treat_every_reponse_as_unique_info'
                                            : 'merge_report_option_same_name_device_id_info'
                                    )
                                }}
                            </p>
                        </div>
                    </div>
                    <div v-if="setting.mergePlayerOption == 'same_name_device_id'" class="mb-2">
                        <Selection
                            v-model="setting.displayMultipleResponseWith"
                            @input="setting.displayMultipleResponseWith = $event"
                            :root="{ backgroundColor: 'rgba(255, 255, 255, .5)' }"
                            :select="{ fontSize: '14px', color: '#3f5c56', textAlignLast: 'left' }"
                            :class="{ 'pointer-events-none': loading }"
                            style="padding: 0.1rem"
                            class="setting-input w-full mt-1"
                            id="merge-player-option"
                        >
                            <option class="text-center" value="best">
                                {{ $t('merge_report_show_best_response') }}
                            </option>
                            <option class="text-center" value="latest">
                                {{ $t('merge_report_show_latest_response') }}
                            </option>
                        </Selection>
                    </div>
                    <slot name="footer"></slot>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import XInput from '@/components/v2/XInput.vue'
import Selection from '@/components/v2/Selection'
import XToggleSwitch from '@/components/v2/XToggleSwitch'

export default {
    props: {
        modalTitle: {
            type: String
        },
        setting: {
            type: Object,
            default: () => ({})
        },

        validateOnClose: {
            type: Boolean,
            default: true
        },

        loading: {
            type: Boolean,
            default: false
        },

        mode: {
            type: String,
            default: 'leaderboard' // leaderboard, report
        }
    },

    components: {
        XInput,
        Selection,
        XToggleSwitch
    },

    watch: {
        setting: {
            handler(value) {
                this.$emit('result', value)
            },
            deep: true
        }
    },

    computed: {
        isLeaderboardMode() {
            return this.mode === 'leaderboard'
        }
    },

    methods: {
        async validate() {
            const validateResult = await this.$validator.validate()

            return validateResult
        },

        async closeModal() {
            if (this.loading) {
                return
            }

            if (this.validateOnClose) {
                if (!(await this.validate())) return
            }

            this.$emit('close')

            this.$emit('result', {
                displayRanking: this.setting.displayRanking,
                reloadTime: this.setting.reloadTime,
                layout: this.setting.layout,
                status: this.setting.status,
                mergePlayerOption: this.setting.mergePlayerOption,
                showRankingTime: this.setting.showRankingTime,
                displayMultipleResponseWith: this.setting.displayMultipleResponseWith
            })
        },

        closeByBackdrop(e) {
            if (!e.target.classList.contains('backdrop')) {
                return
            }

            this.closeModal()
        }
    }
}
</script>

<style lang="scss" scoped>
#merge-report-setting {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 100;

    .backdrop {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;
        background-color: #0006;
        z-index: -1;
    }

    .modal-content {
        display: flex;
        flex-direction: column;
        width: 400px;
        border-radius: 6px;
        color: #333;
        background-color: #fff;
        margin: 0.5rem;

        .modal-header {
            padding: 14px 14px 0 14px;

            h1 {
                margin: 0;
                font-size: 17.5px;
            }

            .close-button {
                width: 28px;
                height: 28px;
                border-radius: 50%;
                color: #6c757d;
                transition: color 0.2s, background-color 0.2s;

                &:hover {
                    color: #343a40;
                    background: #e9ecef;
                }
            }
        }

        .modal-body {
            padding: 14px;

            .setting-input {
                padding: 0.375rem 0.75rem;
                border: 1px solid #ccc;
                font-size: 1rem;
                font-weight: 400;
                color: #000;
                text-shadow: none;
            }

            #display-layout,
            #merge-player-option {
                text-transform: capitalize;
            }
        }
    }
}
</style>
